import React, { useMemo, useState } from "react";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";
import { productDataIncident } from "settings/constants/constants";
import {
  createKontrolTicket,
  createTicket,
  type CreateTicketPayload,
} from "../request/request";
import {
  extractUniqueCustomers,
  filterServicesByCustomer,
  findProductByLabel,
  findTopicByLabel,
  getEndCustomerId,
  getServiceSelectionError,
  isServiceSelectionValid,
} from "../utils/tickets";
import { useLoadServiceItems } from "../hooks/useLoadServiceItems";
import { useTopicsLoader } from "../hooks/useTopicsLoader";
import { useChangeRequestForm } from "../hooks/useChangeRequestForm";
import { KontrolEdgeServiceSelection } from "./shared/KontrolEdgeServiceSelection";
import { RequestModalShell, type RequestStep } from "./requestForm/RequestModalShell";
import { FieldRow, FormField } from "./requestForm/FormField";
import { SummaryFields } from "./requestForm/SummaryFields";
import { AttachmentDropzone } from "./requestForm/AttachmentDropzone";
import { ContactResponseFields } from "./requestForm/ContactResponseFields";
import {
  createEmptyContact,
  isContactValid,
  toContactPayload,
} from "./requestForm/contactDetails";
import { isDescriptionValid, isTitleValid } from "./requestForm/fieldRules";
import { useRequestSteps } from "./requestForm/useRequestSteps";
import { useTicketSubmission } from "./requestForm/useTicketSubmission";
import {
  NewTicketParticipants,
  addParticipantsOnceCreated,
  useNewTicketParticipants,
} from "./requestForm/NewTicketParticipants";

interface IncidentReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTicketCreated?: () => void;
}

// The CTX Reflector desk still expects these two, though they never vary.
const CLOUD = "CloudGlobal";
const REGION = "us-east-1";

/** "Are there service disruptions?" as the Jira option ids it maps to. */
const DISRUPTION_OPTION_ID: Record<string, string> = {
  Yes: "10159",
  No: "10160",
};
const DISRUPTION_LABEL: Record<string, string> = {
  "10159": "Yes",
  "10160": "No",
};
const NO_DISRUPTION = DISRUPTION_OPTION_ID.No;

const INCIDENT_PRIORITY: Record<string, number> = {
  Low: 4,
  Medium: 3,
  High: 2,
  Urgent: 10000,
};

/** Urgent is only for an incident that is disrupting a service. */
const priorityOptionsFor = (affectedServices: string) =>
  affectedServices === NO_DISRUPTION
    ? ["Low", "Medium", "High"]
    : ["Low", "Medium", "High", "Urgent"];

const KE_STEPS: RequestStep[] = [
  { id: "services", title: "Services" },
  { id: "details", title: "Details" },
  { id: "attachments", title: "Files & people" },
];
const REFLECTOR_STEPS: RequestStep[] = [
  { id: "topic", title: "Topic" },
  { id: "details", title: "Details" },
  { id: "attachments", title: "Attachments & contact" },
];

export const IncidentReportModal: React.FC<IncidentReportModalProps> = ({
  isOpen,
  onClose,
  onTicketCreated,
}) => {
  const api = useAxiosAuth0();
  const { tenantId, partnerId } = useAppSelector((state) => state.selection);
  const isPartnerView = tenantId === partnerId;
  const globalProduct = useAppSelector(
    (state) => state.product.selectedProduct
  );
  const isKontrolEdge = globalProduct === "Kontrol Edge";

  const topicsLoader = useTopicsLoader({
    api,
    globalProduct: globalProduct || "",
    isKontrolEdge,
    requestType: "Incident Report",
    products: productDataIncident,
  });
  const form = useChangeRequestForm();
  const { formState } = form;
  // Jira option id ("10159" / "10160"); empty until the user answers.
  const [affectedServices, setAffectedServices] = useState("");
  const [contact, setContact] = useState(createEmptyContact);
  const submission = useTicketSubmission(onTicketCreated);

  const { items: serviceItems } = useLoadServiceItems(
    api,
    tenantId,
    isKontrolEdge
  );
  const uniqueCustomers = useMemo(
    () => extractUniqueCustomers(serviceItems),
    [serviceItems]
  );
  const filteredServiceItems = useMemo(
    () =>
      isPartnerView
        ? filterServicesByCustomer(serviceItems, formState.selectedCustomerId)
        : serviceItems,
    [serviceItems, formState.selectedCustomerId, isPartnerView]
  );

  const priorityOptions = priorityOptionsFor(affectedServices);
  const hasDisruptionAnswer = affectedServices !== "";
  const endCustomerId = getEndCustomerId(
    isPartnerView,
    formState.selectedCustomerId,
    tenantId
  );
  const { participants, setParticipants, resetParticipants } =
    useNewTicketParticipants(isKontrolEdge ? endCustomerId : undefined);
  const steps = isKontrolEdge ? KE_STEPS : REFLECTOR_STEPS;
  const stepper = useRequestSteps(steps, {
    services:
      hasDisruptionAnswer &&
      isServiceSelectionValid(
        isPartnerView,
        formState.selectedCustomerId,
        formState.selectedServiceIds
      ),
    topic:
      hasDisruptionAnswer && topicsLoader.selectedTopicLabel.trim() !== "",
    details:
      priorityOptions.includes(formState.priority) &&
      isTitleValid(formState.title) &&
      isDescriptionValid(formState.description),
    attachments: isKontrolEdge || isContactValid(contact),
  });

  const handleDisruptionChange = (label: string) => {
    const next = DISRUPTION_OPTION_ID[label] ?? "";
    setAffectedServices(next);
    // An Urgent picked earlier stops being an option once the answer is No.
    if (!priorityOptionsFor(next).includes(formState.priority)) {
      form.setPriority("");
    }
  };

  const handleClose = () => {
    topicsLoader.resetTopics();
    form.resetForm();
    setAffectedServices("");
    setContact(createEmptyContact());
    stepper.resetSteps();
    submission.resetSubmission();
  resetParticipants();
    onClose();
  };

  const priorityNumber = INCIDENT_PRIORITY[formState.priority];

  const createKontrolEdgeTicket = () => {
    const serviceError = getServiceSelectionError(
      isPartnerView,
      formState.selectedCustomerId,
      formState.selectedServiceIds
    );
    if (serviceError) {
      submission.refuse(serviceError);
      return;
    }
    void submission.submit(() =>
      createKontrolTicket(api, {
        requesttype: "INCIDENT_REPORT",
        summary: formState.title,
        priority: priorityNumber,
        description: formState.description,
        subscriptionID: formState.selectedSubscriptionIds,
        serviceType: formState.selectedServiceTypes,
        files: formState.attachmentFiles,
        end_customer: endCustomerId,
      }),
      addParticipantsOnceCreated(api, participants)
    );
  };

  const createReflectorTicket = () => {
    // `AffectedServices` is not in `CreateTicketPayload`, and `createTicket`
    // only appends the fields it knows, so it never reaches the backend. It
    // was already so before CTX-1256; kept to leave the payload untouched.
    const payload: CreateTicketPayload & { AffectedServices: string } = {
      Tipo: "Incident Report",
      product:
        findProductByLabel(productDataIncident, globalProduct || "")?.value ??
        "",
      cloud: CLOUD,
      region: REGION,
      TipoSolicitud:
        findTopicByLabel(topicsLoader.topics, topicsLoader.selectedTopicLabel)
          ?.value ?? "",
      AffectedServices: affectedServices,
      Resumen: formState.title,
      Prioridad: priorityNumber,
      Descripcion: formState.description,
      files: formState.attachmentFiles,
      ...(contact.sendResponse ? toContactPayload(contact) : {}),
    };
    void submission.submit(() => createTicket(api, payload));
  };

  const disruptionField = (
    <FormField
      label="Are there service disruptions?"
      required
      hint="Answer Yes if a service is down or degraded right now."
    >
      <GenericDropdown
        items={["Yes", "No"]}
        value={DISRUPTION_LABEL[affectedServices] ?? ""}
        onSelect={handleDisruptionChange}
        defaultLabel="Select"
        minWidthClass=""
        maxWidthClass=""
        widthClass="w-full"
        fixedPosition={true}
        dropdownMaxHeightClass="max-h-48"
      />
    </FormField>
  );

  return (
    <RequestModalShell
      type="incident"
      isOpen={isOpen}
      onClose={handleClose}
      steps={steps}
      activeStepId={stepper.activeStepId}
      canActivate={stepper.canActivate}
      onStepChange={stepper.goToStep}
      canContinue={stepper.canContinue}
      isSaving={submission.isSaving}
      onSubmit={isKontrolEdge ? createKontrolEdgeTicket : createReflectorTicket}
      submitError={submission.submitError}
      created={submission.created}
    >
      {stepper.activeStepId === "services" && (
        <>
          <KontrolEdgeServiceSelection
            isPartnerView={isPartnerView}
            uniqueCustomers={uniqueCustomers}
            filteredServiceItems={filteredServiceItems}
            selectedCustomerId={formState.selectedCustomerId}
            selectedCustomerNameDisplay={formState.selectedCustomerNameDisplay}
            selectedServiceLabels={formState.selectedServiceLabels}
            onCustomerSelect={(name) =>
              form.handleCustomerSelect(name, uniqueCustomers)
            }
            onServiceSelect={(labels) =>
              form.handleServiceSelect(labels, filteredServiceItems)
            }
            servicesHint="The service(s) affected by the incident."
          />
          <FieldRow>{disruptionField}</FieldRow>
        </>
      )}

      {stepper.activeStepId === "topic" && (
        <FieldRow>
          <FormField label="Topic" required>
            <GenericDropdown
              items={topicsLoader.topics.map((t) => t.label)}
              value={topicsLoader.selectedTopicLabel}
              onSelect={topicsLoader.setSelectedTopicLabel}
              loading={topicsLoader.loadingTopics}
              defaultLabel="Select a topic"
              minWidthClass=""
              maxWidthClass=""
              widthClass="w-full"
              fixedPosition={true}
              dropdownMaxHeightClass="max-h-48"
            />
          </FormField>
          {disruptionField}
        </FieldRow>
      )}

      {stepper.activeStepId === "details" && (
        <>
          <FieldRow>
            <FormField
              label="Priority"
              required
              hint={
                affectedServices === NO_DISRUPTION
                  ? "Urgent is for incidents that disrupt a service."
                  : undefined
              }
            >
              <GenericDropdown
                items={priorityOptions}
                value={formState.priority}
                onSelect={form.setPriority}
                defaultLabel="Select priority"
                minWidthClass=""
                maxWidthClass=""
                widthClass="w-full"
                fixedPosition={true}
              />
            </FormField>
          </FieldRow>
          <SummaryFields
            title={formState.title}
            description={formState.description}
            onTitleChange={form.setTitle}
            onDescriptionChange={form.setDescription}
            titlePlaceholder="What is not working?"
            descriptionPlaceholder="What happened, since when, and what you already tried"
          />
        </>
      )}

      {stepper.activeStepId === "attachments" && (
        <>
          <AttachmentDropzone
            files={formState.attachmentFiles}
            onChange={form.setAttachmentFiles}
            hint="Screenshots, logs or traces that show the problem."
          />
          {!isKontrolEdge && (
            <ContactResponseFields contact={contact} onChange={setContact} />
          )}
          {isKontrolEdge && (
            <NewTicketParticipants
              endCustomerId={endCustomerId}
              selected={participants}
              onChange={setParticipants}
            />
          )}
        </>
      )}
    </RequestModalShell>
  );
};

export default IncidentReportModal;
